// Accepts the project's VolumeItem shape loosely; consumers across the app
// sometimes use local interfaces with slightly different dimension unit typing.
interface VolumeItemLike {
  type?: string
  quantity?: number
  weight?: number
  dimensions?: {
    length: number
    width: number
    height: number
    unit: string
  }
}

export interface CargoMetrics {
  totalWeightKg: number
  totalCubicMeters: number
  containerCount: number
  airChargeableKg: number    // max(weight, cubage * 166.66) — IATA 1:6
  lclChargeableKg: number    // max(weight, cubage * 300)     — LCL 1:3.33
  wmTonOrM3: number          // max(weight/1000, cubage)      — W/M
}

const EMPTY: CargoMetrics = {
  totalWeightKg: 0,
  totalCubicMeters: 0,
  containerCount: 0,
  airChargeableKg: 0,
  lclChargeableKg: 0,
  wmTonOrM3: 0,
}

/**
 * Aggregate quote.volume_items into the cargo dimensions needed to compute
 * fee subtotals (per-kg, per-m³, W/M, per-container, etc).
 *
 * Container counts only when volumeName === 'FCL': for FCL the `type` field is
 * the container size (20'GP, 40'HC...) and `quantity` is the container count.
 * For LCL the items represent loose volumes (cartons/pallets).
 */
export function computeCargoMetrics(
  items: VolumeItemLike[] | undefined | null,
  volumeName?: 'FCL' | 'LCL' | string | null
): CargoMetrics {
  if (!items || items.length === 0) return { ...EMPTY }

  let totalWeightKg = 0
  let totalCubicMeters = 0
  let containerCount = 0
  const isFCL = volumeName === 'FCL'

  for (const item of items) {
    const qty = item.quantity || 0
    totalWeightKg += (item.weight || 0) * qty

    if (item.dimensions) {
      const { length, width, height, unit } = item.dimensions
      const toM = unit === 'cm' ? 0.01 : 1
      const m3PerItem = (length * toM) * (width * toM) * (height * toM)
      totalCubicMeters += m3PerItem * qty
    }

    if (isFCL) containerCount += qty
  }

  return {
    totalWeightKg,
    totalCubicMeters,
    containerCount,
    airChargeableKg: Math.max(totalWeightKg, totalCubicMeters * 166.66),
    lclChargeableKg: Math.max(totalWeightKg, totalCubicMeters * 300),
    wmTonOrM3: Math.max(totalWeightKg / 1000, totalCubicMeters),
  }
}
